'use client'
import styles from '@/components/Footer/footer.module.css'
import { useState, useEffect, useRef } from 'react'

// The interactive island of the footer: everything else in Footer.js renders
// on the server. Keeps the Turnstile + submit logic identical to the old form.
export default function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        website: '',
    })
    const [turnstileToken, setTurnstileToken] = useState('')
    const [status, setStatus] = useState('')

    const widgetIdRef = useRef(null)

    // The footer is on EVERY page, so nothing in here may throw during render:
    // a missing key or a Turnstile hiccup must disable the form, never blank the
    // site. (It used to `throw` on a missing key — one bad build took down every
    // page with "a client-side exception has occurred".)
    const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''

    useEffect(() => {
        if (!SITE_KEY) {
            console.warn('Turnstile disabled: NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set')
            return
        }
        let cancelled = false
        const inject = () => {
            if (cancelled || !window.turnstile) return
            try {
                widgetIdRef.current = window.turnstile.render('#cf-turnstile', {
                    sitekey: SITE_KEY,
                    theme: 'dark',
                    size: 'flexible',
                    // silent when it passes; shows only when Cloudflare wants a click
                    appearance: 'interaction-only',
                    callback: (token) => setTurnstileToken(token),
                    'error-callback': () => setTurnstileToken(''),
                    'expired-callback': () => setTurnstileToken(''),
                })
            } catch (err) {
                // e.g. the key isn't valid for this hostname (staging on a bare
                // IP). Leave the form disabled rather than crashing the page.
                console.warn('Turnstile could not render:', err)
            }
        }

        if (window.turnstile) {
            inject()
        } else {
            const s = document.createElement('script')
            s.src =
                'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
            s.async = true
            s.onload = inject
            s.onerror = () => console.warn('Turnstile script failed to load')
            document.body.appendChild(s)
        }
        return () => {
            cancelled = true
        }
    }, [SITE_KEY])

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((p) => ({ ...p, [name]: value }))
        // Editing after a result clears it, so the CTA reads "Send message" again.
        setStatus((s) => (s === 'error' || s === 'success' ? '' : s))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setStatus('sending')

        if (!turnstileToken) {
            // no widget (key missing / blocked) or not solved yet
            setStatus('error')
            return
        }

        try {
            const res = await fetch('/api/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, turnstileToken }),
            })
            if (!res.ok) throw new Error(`Status ${res.status}`)
            setStatus('success')
            setFormData({ name: '', email: '', message: '', website: '' })
            setTurnstileToken('')
            try {
                window.turnstile?.reset(widgetIdRef.current)
            } catch {
                // widget already gone — nothing to reset
            }
        } catch (err) {
            console.error(err)
            setStatus('error')
        }
    }

    return (
        <div id="contact-form" className={styles.formCard}>
            <h3 className={styles.formTitle}>Send us a message</h3>
            <p className={styles.formSub}>
                Questions about a car or a booking? We usually reply within a couple of hours.
            </p>
            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.honeypot} aria-hidden="true">
                    <label htmlFor="website">Your website</label>
                    <input
                        id="website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website}
                        onChange={handleChange}
                    />
                </div>

                <div className={styles.formGrid}>
                    <input
                        name="name"
                        type="text"
                        placeholder="Name*"
                        aria-label="Name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                    />
                    <input
                        name="email"
                        type="email"
                        placeholder="Email*"
                        aria-label="Email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                    />
                </div>
                <textarea
                    name="message"
                    placeholder="Message*"
                    aria-label="Message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                />

                <div id="cf-turnstile" className={styles.turnstileSlot} />

                <button
                    type="submit"
                    className={styles.sendButton}
                    disabled={status === 'sending'}
                >
                    {status === 'sending'
                        ? 'Sending…'
                        : status === 'success'
                            ? 'Sent!'
                            : status === 'error'
                                ? 'Failed to send'
                                : 'Send message'}
                </button>
                {status === 'error' && (
                    <span className={styles.formError}>
                        Something went wrong. Please try again.
                    </span>
                )}
                {status === 'success' && (
                    <span className={styles.formSuccess}>
                        Thanks — your message is on its way.
                    </span>
                )}
            </form>
        </div>
    )
}
